import { AxisTickProps } from "@nivo/axes";
import { ScaleValue } from "@nivo/scales";
import { DOT_SIZE } from "constants/booksHomePage";
import { colors } from "constants/colors";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import { ReactNode } from "react";

export type GenericBarChartData = {
  date: string;
  bar1: number;
  bar2: number;
  lineValue?: number;
};

export type BarForecastMeta = {
  isForecast?: boolean;
  hasOverlapActual?: boolean;
  actualBar1?: number;
  actualBar2?: number;
  actualLineValue?: number;
};

export type ForecastMetaByDate = Record<string, BarForecastMeta>;

export const FORECAST_INDICATOR_COLOR = colors.green[30];

export const buildForecastMetaByDate = <T,>(
  items: T[],
  getDateKey: (item: T) => string,
  getForecastFields: (item: T) => BarForecastMeta
): { forecastMetaByDate: ForecastMetaByDate; hasForecastData: boolean } => {
  const forecastMetaByDate: ForecastMetaByDate = {};
  let hasForecastData = false;

  items.forEach((item) => {
    const meta = getForecastFields(item);
    forecastMetaByDate[getDateKey(item)] = meta;
    if (meta.isForecast) hasForecastData = true;
  });

  return { forecastMetaByDate, hasForecastData };
};

export type BarChartConfig = {
  colors: {
    bar1: string;
    bar1CrossLine: string;
    bar1CrossLineOpacity?: number;
    bar2: string;
    bar2CrossLine: string;
    bar2CrossLineOpacity?: number;
    line: string;
  };
  labels: {
    bar1: string;
    bar2: string;
    line: string;
    lineNegative?: string;
  };
};

export const MonthYearChartBottomAxisTicks = <Value extends ScaleValue>(
  tick: AxisTickProps<Value> & {
    data_length: number;
  }
) => {
  const isValidDate = dayjs(tick.value as string).isValid();
  const isLast = tick.tickIndex === tick.data_length - 1;
  const isFirst = tick.tickIndex === 0;

  if (isFirst || isLast) {
    return (
      <motion.g
        initial={{
          opacity: 0,
          x: 0,
          y: 0,
        }}
        animate={{
          opacity: 1,
          x: tick.x,
          y: tick.y,
        }}
      >
        <motion.text
          dy={40}
          textAnchor="middle"
          fontSize={12}
          fill={colors.text[30]}
        >
          {isValidDate ? (
            <>{dayjs(tick.value as string).format("MMM YY")}</>
          ) : (
            (tick.value as string)
          )}
        </motion.text>
      </motion.g>
    );
  }

  return <></>;
};

export const LegendDot = ({ color }: { color: string }) => {
  return (
    <div
      className="t-rounded-full"
      style={{
        background: color,
        width: DOT_SIZE,
        height: DOT_SIZE,
      }}
    />
  );
};

export const Legend = ({ dot, lable }: { dot: ReactNode; lable: string }) => {
  return (
    <div className="t-flex t-gap-2 t-items-center t-text-body-sm t-text-text-30">
      {dot}
      {lable}
    </div>
  );
};

export const ForecastLegend = ({ show }: { show: boolean }) => {
  if (!show) return null;

  return (
    <Legend
      dot={<LegendDot color={FORECAST_INDICATOR_COLOR} />}
      lable="Forecasted"
    />
  );
};

export const ChartItemInfo = ({
  dot,
  lable,
  value,
}: {
  dot: ReactNode;
  lable: string;
  value: ReactNode;
}) => {
  return (
    <div className="t-flex t-gap-2 t-items-center t-justify-between t-text-body-sm">
      <div className="t-flex t-gap-2 t-items-center">
        {dot}
        <span className="t-text-neutral-20">{lable}: </span>
      </div>
      <div className="t-text-white">{value}</div>
    </div>
  );
};

export const CATEGORY_COLORS = [
  colors.purple[40],
  colors.purple[20],
  colors.red[40],
  colors.blue[50],
  colors.green[50],
  colors.neutral[40],
];

export const formatAxisValue = (value: number): string => {
  const absValue = Math.abs(value);
  if (absValue >= 1_000_000) {
    return `$${(absValue / 1_000_000).toFixed(1)}M`;
  }
  if (absValue >= 1_000) {
    return `$${Math.round(absValue / 1_000)}K`;
  }
  return `$${absValue}`;
};
